import {
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Text } from "@chakra-ui/react";

import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import { usePrepApolloData } from "components/Shared/ChakraReactTable/utils";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import {
  formatIntegerCell,
  formatJustifiedDateTimeCell,
  makeFormatDateCell,
} from "components/Shared/ChakraReactTable/formatters";
import { useCurrentBrandId } from "utils/currentUserHooks";
import Link from "components/Shared/Link";
import { brandRetailerPage } from "utils/links";
import FullPageTable, {
  useFullPageTableProps,
} from "components/Shared/ChakraReactTable/FullPageTable";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import {
  BrandRetailerFilter,
  BrandRetailersOrderBy,
  BrandRetailerStatus,
} from "__generated__/gql/graphql";
import BrandRetailerStatusBadge from "components/Shared/BrandRetailerStatus/Badge";
import IsCustomBadge from "components/Shared/IsCustomBadge";
import { NameWithFlagIcons } from "components/Shared/RetailerDirectory/utils";
import RetailerTypeBadge from "components/Shared/RetailerDirectory/RetailerTypeBadge";
import { BrandRetailerBrokerIcons } from "components/Shared/RetailerDirectory/RetailerBrokerIcons";
import useArchivedRowProps from "components/Shared/ChakraReactTable/useArchivedRowProps";
import SyncIndicator from "components/Shared/SyncIndicator";

const fragment = gql(/* GraphQL */ `
  fragment BrandRetailersTableRow on BrandRetailer {
    id
    status
    startDate
    endDate
    numStores
    createdAt
    analyticsSyncDetails {
      ...SyncIndicator
    }
    retailer {
      id
      nameWithUniqueSuffix
      isCustom
      retailerType
      ...RetailerNameWithFlagIconsFragment
    }
    accountOwner {
      id
      email
      fullName
    }
    brandRetailerComments {
      aggregates {
        max {
          createdAt
        }
      }
    }
    ...BrandRetailerBrokerIcons
  }
`);

const query = gql(/* GraphQL */ `
  query BrandRetailersTable(
    $brandId: UUID!
    $limit: Int = 10
    $offset: Int = 0
    $filter: BrandRetailerFilter
    $orderBy: [BrandRetailersOrderBy!] = CREATED_AT_ASC
  ) {
    brandRetailers(
      condition: { brandId: $brandId }
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      totalCount
      nodes {
        ...BrandRetailersTableRow
      }
    }
  }
`);

type Datum = DocumentType<typeof fragment>;

const col = createColumnHelper<Datum>();

const columns = [
  col.accessor((r) => r.retailer?.nameWithUniqueSuffix, {
    id: "retailerName",
    header: "Retailer Name",
    cell: (cell) => (
      <Link href={brandRetailerPage(cell.row.original.retailer?.id)}>
        <NameWithFlagIcons
          retailer={cell.row.original.retailer}
          lineHeight="normal"
          nameProps={{ mr: "auto" }}
        >
          <BrandRetailerBrokerIcons brandRetailer={cell.row.original} />
          <SyncIndicator
            analyticsSyncDetails={cell.row.original.analyticsSyncDetails}
            ml={0}
          />
        </NameWithFlagIcons>
      </Link>
    ),
    minSize: 200,
  }),
  col.accessor("status", {
    header: "Status",
    cell: (cell) => (
      <BrandRetailerStatusBadge status={cell.getValue()} mt="-.25em" />
    ),
    size: 130,
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor("startDate", {
    header: "Start Date",
    cell: makeFormatDateCell("PP"),
    size: 140,
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor("endDate", {
    header: "End Date",
    cell: makeFormatDateCell("PP"),
    size: 140,
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor("numStores", {
    header: "Store Count",
    cell: formatIntegerCell,
    size: 150,
    meta: {
      headerProps: { textAlign: "center" },
      cellProps: { isNumeric: true },
    },
  }),
  col.accessor((r) => r.accountOwner?.fullName || r.accountOwner?.email, {
    id: "accountOwnerName",
    header: "Account Owner",
    size: 175,
    meta: {
      props: { textAlign: "center" },
      cellProps: { isTruncated: true },
    },
  }),
  col.accessor((r) => r.retailer?.retailerType, {
    id: "retailerType",
    header: "Retailer Type",
    cell: (cell) => {
      const retailerType = cell.getValue();
      if (!retailerType) return null;
      return (
        <RetailerTypeBadge
          retailerType={retailerType}
          isTruncated
          maxW="100%"
          fontSize=".8em"
          lineHeight={1.5}
        />
      );
    },
    size: 155,
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor((r) => r.retailer?.isCustom, {
    id: "isCustom",
    header: "Custom?",
    cell: (cell) => <IsCustomBadge isCustom={cell.getValue()} />,
    size: 120,
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor((r) => r.brandRetailerComments.aggregates?.max?.createdAt, {
    id: "lastStatusUpdateAt",
    header: "Last Status Update",
    cell: formatJustifiedDateTimeCell,
    size: 210,
    meta: {
      headerProps: { textAlign: "center" },
      cellProps: { textAlign: "right", fontSize: "sm" },
    },
  }),
  col.accessor("createdAt", {
    header: "Added",
    cell: formatJustifiedDateTimeCell,
    size: 210,
    meta: {
      headerProps: { textAlign: "center" },
      cellProps: { textAlign: "right", fontSize: "sm" },
    },
  }),
];

const columnPinning: ColumnPinningState = { left: ["retailerName"] };

const orderByEnumsById: GQLOrderByEnumsById<BrandRetailersOrderBy> = {
  retailerName: [
    BrandRetailersOrderBy.RETAILER__NAME_ASC,
    BrandRetailersOrderBy.RETAILER__NAME_DESC,
  ],
  status: [BrandRetailersOrderBy.STATUS_ASC, BrandRetailersOrderBy.STATUS_DESC],
  startDate: [
    BrandRetailersOrderBy.START_DATE_ASC,
    BrandRetailersOrderBy.START_DATE_DESC,
  ],
  endDate: [
    BrandRetailersOrderBy.END_DATE_ASC,
    BrandRetailersOrderBy.END_DATE_DESC,
  ],
  numStores: [
    BrandRetailersOrderBy.NUM_STORES_ASC,
    BrandRetailersOrderBy.NUM_STORES_DESC,
  ],
  accountOwnerName: [
    BrandRetailersOrderBy.ACCOUNT_OWNER__EMAIL_ASC,
    BrandRetailersOrderBy.ACCOUNT_OWNER__EMAIL_DESC,
  ],
  retailerType: [
    BrandRetailersOrderBy.RETAILER__RETAILER_TYPE_ASC,
    BrandRetailersOrderBy.RETAILER__RETAILER_TYPE_DESC,
  ],
  isCustom: [
    BrandRetailersOrderBy.RETAILER__IS_CUSTOM_ASC,
    BrandRetailersOrderBy.RETAILER__IS_CUSTOM_DESC,
  ],
  lastStatusUpdateAt: [
    BrandRetailersOrderBy.BRAND_RETAILER_COMMENTS_MAX_CREATED_AT_ASC,
    BrandRetailersOrderBy.BRAND_RETAILER_COMMENTS_MAX_CREATED_AT_DESC,
  ],
  createdAt: [
    BrandRetailersOrderBy.CREATED_AT_ASC,
    BrandRetailersOrderBy.CREATED_AT_DESC,
  ],
};

export type Props = {
  filter?: BrandRetailerFilter;
  isReady?: boolean;
};

export default function BrandRetailersTable({ filter, isReady = true }: Props) {
  const brandId = useCurrentBrandId();
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination({ pageSize: 50 });

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "createdAt", desc: false }],
  );

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const {
    data: rawData,
    loading,
    refetch,
  } = useBrandQuery(query, {
    skip: !brandId || isPendingFilterReset || isPendingSortingReset || !isReady,
    variables: brandId
      ? { brandId, orderBy, filter, ...limitOffset }
      : undefined,
    fetchPolicy: "cache-and-network",
  });

  const { nodes, totalCount } = rawData?.brandRetailers || {};

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(getFragmentData(fragment, nodes)),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning, pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const tableProps = useFullPageTableProps<Datum>([
    useFixedLayout(tableInstance),
    useArchivedRowProps(
      (r) => r.original.status === BrandRetailerStatus.INACTIVE,
    ),
    {
      tableProps: { fontSize: "sm" },
      tableBodyProps: { sx: { "> tr > td": { py: 2 } } },
    },
  ]);

  const isLoading = loading || typeof window === "undefined" || !isReady;

  return (
    <FullPageTable
      {...{ tableInstance }}
      {...tableProps}
      isLoading={isLoading}
      totalCount={totalCount}
      resultTypeLabel="retailers"
      onRefresh={refetch}
      noRowsPlaceholder={
        <Text>
          No retailers {filter ? "found" : "have been added to your account"}.
        </Text>
      }
    />
  );
}
